import { groupOrg, type GroupBy, type GroupNode, type OrgModel, type SquadDirectory } from '@tive/core';
import { defaultFoldedGroups } from './group-layout';

/**
 * The tree of cards the team board draws, from the two places cards come from.
 *
 * `groupOrg` builds its tree out of people, so a team nobody is on yet produces
 * no node at all — and a team somebody has just made would vanish until the
 * first person joined it. Those are folded back in from the directory here,
 * rather than in the component, because the PNG export has to lay out exactly
 * the same tree to know how big the picture is.
 */
export interface GroupTree {
  nodes: Map<string, GroupNode>;
  rootIds: string[];
}

export interface GroupTreeOptions {
  model: OrgModel;
  levels: GroupBy[];
  squads: SquadDirectory;
  focusId: string | null;
}

export function buildGroupTree({ model, levels, squads, focusId }: GroupTreeOptions): GroupTree {
  const grouped = groupOrg(model, { levels, squads, rootId: focusId });

  const nodes = new Map<string, GroupNode>();
  for (const [id, node] of grouped.byId) nodes.set(id, { ...node, childIds: [...node.childIds] });
  const rootIds = [...grouped.rootIds];

  if (!levels.includes('squad')) return { nodes, rootIds };

  /*
   * An empty team is keyed by its squad rather than by a path, because nobody
   * walks one to it. Its parent is whichever card carries that squad's name,
   * which is what puts a group made before anybody joined it *inside* its
   * parent rather than beside it as an unrelated tree.
   */
  const cardFor = new Map<string, string>();
  for (const [id, node] of grouped.byId) {
    if (node.key === 'squad' && !cardFor.has(node.label)) cardFor.set(node.label, id);
  }
  const squadById = new Map(squads.squads.map((squad) => [squad.id, squad]));

  /*
   * A team needs one of these only when the grouped tree produced no card for
   * it at all — which is not the same question as whether anybody is *directly*
   * on it.
   *
   * A team that holds other teams and nobody of its own still gets a real card,
   * because everybody on a sub-team walks a path through it. Asking about its
   * own members instead drew it twice: once with its sub-teams hanging off it,
   * and once as an empty card beside them saying "Nobody on this team yet".
   * That is what happened the moment somebody made a group and moved teams into
   * it, which is the whole point of making one.
   */
  const missing = squads.squads.filter((squad) => !cardFor.has(squad.name));

  for (const squad of missing) {
    nodes.set(`empty:${squad.id}`, {
      id: `empty:${squad.id}`,
      key: 'squad',
      label: squad.name,
      depth: 0,
      childIds: [],
      members: [],
      headcount: 0,
    });
  }

  for (const squad of missing) {
    const id = `empty:${squad.id}`;
    const parentSquad = squad.parentId ? squadById.get(squad.parentId) : undefined;
    const parentId = parentSquad ? (cardFor.get(parentSquad.name) ?? `empty:${parentSquad.id}`) : null;
    const parent = parentId ? nodes.get(parentId) : undefined;

    if (parent) parent.childIds.push(id);
    else rootIds.push(id);
  }

  return { nodes, rootIds };
}

/**
 * Which grouping a set of folds belongs to.
 *
 * A card's id is its path through the levels, so every id is meaningless the
 * moment the levels change. Keying the folds by the grouping keeps each one's
 * arrangement instead of throwing it away — switch to Location and back and the
 * board is as you left it — and, more importantly, makes "nothing is folded" a
 * different answer from "nothing is known", which a bare list cannot express.
 */
export function groupingKey(levels: readonly GroupBy[], focusId: string | null): string {
  return `${levels.join('|')}::${focusId ?? ''}`;
}

/**
 * Which cards are folded: what this browser remembers for this grouping, or the
 * default when it remembers nothing.
 *
 * `undefined` is no answer and falls through to the default; an empty array is
 * somebody having opened everything, and is obeyed. Getting that distinction
 * wrong is not a subtle bug — folding one card re-derived the default for every
 * other card on the board, so the first click unfolded the entire company.
 */
export function resolveFolded(tree: GroupTree, remembered: readonly string[] | undefined): Set<string> {
  if (!remembered) return defaultFoldedGroups(tree.nodes);
  return new Set(remembered.filter((id) => tree.nodes.has(id)));
}
